import type { Addon, Agreement, AgreementType, CalculateDto, CalculateResponse, SamAgreement } from "@/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import localStore from '@/stores/local'
import { addMonths, differenceInCalendarMonths } from "date-fns";
import axios from "axios";
import local from "@/stores/local";
import { useMessageStore } from "./messageStore";

export const useAgreementStore = defineStore('agreement', () => {
  const message = useMessageStore()

  const agreement = ref<Partial<Agreement>>({})
  const agreementType = ref<AgreementType | null>()
  const calculation = ref<CalculateResponse | null>()
  const calculating = ref(false)
  const isOkay = ref(false);
  const selectedAddons = ref<Addon[]>([])
  const samAgreements = ref<SamAgreement[] | null>()
  const addonsPrice = computed<number>(() => selectedAddons.value.reduce((prev, current) => prev + current.price, 0))
  const includedAddons = computed<Addon[]>(() => calculation.value?.additional?.filter(addon => agreementType.value?.includeAddons.includes(addon.name)) ?? [] )

  const basePrice = computed<number>(() => {
    if (!calculation.value) return 0

    return calculation.value.servicePlanPrice + includedAddons.value.reduce((prev, current) => prev + current.price, 0)
  })

  const nextPayment = computed<number | undefined>(() => {
    if (!agreement.value.nextServicePaymentMethod) return undefined

    const totalPrice = basePrice.value + addonsPrice.value

    switch (agreement.value.nextServicePaymentMethod) {
      case "cash":
        return totalPrice

      case "include":
        const months = differenceInCalendarMonths(addMonths(new Date(), 1), new Date(agreement.value.startYear as number, agreement.value.startMonth as number)) + 1 // Don't know why
        return totalPrice * months

      default:
        return undefined
    }
  })

  async function calculate() {
    calculating.value = true
    // reset the value
    isOkay.value = false
    let { brandId, modelId, engineId, registrationYear, duration, yearlyMileage, oldMileage } = agreement.value

    // Vehicle pages calculate before duration/yearlyMileage are chosen, so use placeholders until then.
    // Check for null, not falsy — 0 km is a real old mileage (every current-year car starts at 0).
    if(duration == null || yearlyMileage == null || oldMileage == null)
    {
      duration = 15
      yearlyMileage = 10000
      oldMileage = 100000
    }

    if (brandId == null || modelId == null || engineId == null) throw 'Brand, model and engine IDs must be set!'
    if (duration == null) throw 'Duration must be set'
    if (yearlyMileage == null) throw 'yearlyMileage must be set'

    const calculateDto: CalculateDto = {
      brandId: agreementType.value?.customBrandId ?? brandId,
      modelId: agreementType.value?.customModelId ?? modelId,
      engineId: agreementType.value?.customEngineId ?? engineId,
      duration,
      yearlyMileage
    };

    if (registrationYear !== new Date().getFullYear()) {
      if (registrationYear == null && oldMileage == null) throw 'Tried to set registrationYear and oldMileage, but something is missing'

      calculateDto.registrationYear = registrationYear
      calculateDto.oldMileage = oldMileage
    }

    try {
      
      const { data } = await axios.post('proxy/calculate', calculateDto)

      calculation.value = data
      local.setItem('calculation', JSON.parse(JSON.stringify(data)))
      // used to tell if you can go to the next page or not
      isOkay.value = true;
      return data
    } catch (error) {
      console.error(error)
      message.addError('Der skete en fejl ved beregning af pris. Prøv at gå et skridt tilbage og frem igen. Hvis problemet fortsætter kan du kontakte os direkte.')
      isOkay.value = false;
    } finally {
      calculating.value = false
    }
  }

  async function getSavedState() {
    agreement.value = await localStore.getItem<Partial<Agreement>>('agreement') ?? {}
    agreementType.value = await localStore.getItem<AgreementType>('agreementType')
    calculation.value = await localStore.getItem<CalculateResponse>('calculation')
    selectedAddons.value = await localStore.getItem<Addon[]>('selectedAddons') ?? []
    samAgreements.value = await localStore.getItem<SamAgreement[]>('samAgreements')

    // A persisted calculation means the price was already valid — restore the gate
    // so a browser refresh doesn't require nudging the slider to recalculate.
    isOkay.value = calculation.value != null
  }

  function reset() {
    agreement.value = {}
    agreementType.value = null
    calculation.value = null
    selectedAddons.value = []
    samAgreements.value = null
  }

  return {
    agreement,
    agreementType,
    calculation,
    calculating,
    selectedAddons,
    includedAddons,
    basePrice,
    addonsPrice,
    nextPayment,
    samAgreements,
    getSavedState,
    calculate,
    reset,
    isOkay
  }
})